import { XClose } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment-timezone';
import React, { useEffect, useRef } from 'react';

export interface BubbleNotification {
    id: number;
    type: string;
    title: string;
    orderId: string;
    description: string;
    createdAt: string;
    metadata: any;
    referenceId: number;
    moduleName: string;
}

interface IProps {
    notification: BubbleNotification;
    isLeaving: boolean;
    timezone?: string;
    onDismiss: (id: number) => void;
    onClick: (notification: BubbleNotification) => void;
}

const AUTO_DISMISS_MS = 3000;

const NotificationBubble = ({ notification, isLeaving, timezone, onDismiss, onClick }: IProps) => {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isHoveredRef = useRef(false);
    // Keep a stable ref to onDismiss to avoid stale-closure issues in the timer callback
    const onDismissRef = useRef(onDismiss);

    useEffect(() => {
        onDismissRef.current = onDismiss;
    }, [onDismiss]);

    const clearTimer = () => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const scheduleTimer = () => {
        clearTimer();
        timerRef.current = setTimeout(() => {
            if (!isHoveredRef.current) {
                onDismissRef.current(notification.id);
            }
        }, AUTO_DISMISS_MS);
    };

    // Start auto-dismiss timer on mount
    useEffect(() => {
        scheduleTimer();

        return clearTimer;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear timer when parent triggers the leave animation (avoids double-dismiss)
    useEffect(() => {
        if (isLeaving) clearTimer();
    }, [isLeaving]);

    const handleMouseEnter = () => {
        isHoveredRef.current = true;
        clearTimer();
    };

    const handleMouseLeave = () => {
        isHoveredRef.current = false;
        // Restart the full 3s window after the user stops hovering
        scheduleTimer();
    };

    const timeStr = notification.createdAt ? (moment as any).tz(moment(notification.createdAt), timezone || 'America/New_York').format('h:mm A') : '';

    return (
        <div
            className={`notification-bubble ${isLeaving ? 'notification-bubble-leave' : 'notification-bubble-enter'}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="status"
            aria-live="polite"
        >
            {/* Header row: type label + green dot + time + close */}
            <div className="flex items-center gap-x-1.5 mb-1.5">
                <span className="text-textSecondary font-medium text-[13px] leading-5 first-letter:capitalize lowercase truncate">{notification.type}</span>
                <span className="w-1.5 h-1.5 bg-successSecondary rounded-full flex-shrink-0" aria-hidden="true" />
                <span className="text-grayLight600 text-[12px] leading-[18px] font-normal flex-1">{timeStr}</span>
                <button
                    type="button"
                    aria-label="Dismiss notification"
                    className="flex-shrink-0 text-grayLight400 hover:text-grayLight600 transition-colors"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDismiss(notification.id);
                    }}
                >
                    <XClose className="w-4 h-4" />
                </button>
            </div>

            {/* Clickable body — navigates to the related entity */}
            <div className="cursor-pointer" onClick={() => onClick(notification)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClick(notification)}>
                {/* Title + order code */}
                <div className="flex flex-wrap items-baseline gap-x-1 mb-0.5">
                    {notification.title && <span className="text-textSecondary font-semibold text-[13px] leading-5">{notification.title}</span>}
                    {notification.orderId && <span className="text-primary700 font-semibold text-[12px] leading-5">{notification.orderId}</span>}
                </div>

                {/* Description — capped at 2 lines */}
                {notification.description && <p className="text-grayLight600 text-[12px] leading-[18px] font-normal line-clamp-2">{notification.description}</p>}
            </div>
        </div>
    );
};

export default NotificationBubble;
